import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/EnterpriseAuthContext";

const FEEDBACK_CATEGORIES = [
  { value: "bug", label: "Bug report" },
  { value: "feature", label: "Feature request" },
  { value: "general", label: "General feedback" },
];

/**
 * Send Feedback dialog for collecting user feedback and storing it in Supabase.
 * @param {object} props - Component props.
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {() => void} props.onClose - Callback to close the dialog.
 * @param {(message: string) => void} [props.onSuccess] - Optional callback when feedback is submitted successfully.
 * @returns {JSX.Element} Send Feedback dialog.
 */
function SendFeedback({ open, onClose, onSuccess }) {
  const { state: authState } = useAuth();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setMessage("");
      setCategory("");
      setEmail("");
      setError("");
    }
  }, [open]);

  /**
   * Handle form submission to Supabase.
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      setError("Please enter your feedback message.");
      return;
    }

    const userId = authState.user?.id;
    if (!userId) {
      setError("You must be signed in to submit feedback.");
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      const { error: insertError } = await supabase.from("feedback").insert([
        {
          user_id: userId,
          message: trimmedMessage,
          category: category || null,
          email: email.trim() || null,
        },
      ]);

      if (insertError) throw insertError;

      setSnackbarOpen(true);
      onSuccess?.("Thank you! Your feedback has been submitted.");
      onClose();
    } catch (err) {
      console.error("Error submitting feedback:", err);
      setError(err.message || "Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
        aria-labelledby="send-feedback-dialog-title"
        aria-describedby="send-feedback-dialog-description"
        disableRestoreFocus
      >
        <DialogTitle id="send-feedback-dialog-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box component="span" sx={{ flex: 1 }}>Send feedback</Box>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close feedback dialog"
            disabled={submitting}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography id="send-feedback-dialog-description" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tell us what is working, what is not, or what you would like Quill AI to do next.
          </Typography>

          <Box sx={{ display: "grid", gap: 2 }}>
            <Box role="radiogroup" aria-label="Category" sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {FEEDBACK_CATEGORIES.map((cat) => {
                const selected = category === cat.value;
                return (
                  <Chip
                    key={cat.value}
                    label={cat.label}
                    role="radio"
                    aria-checked={selected}
                    clickable
                    color={selected ? "primary" : "default"}
                    variant={selected ? "filled" : "outlined"}
                    onClick={() => setCategory(selected ? "" : cat.value)}
                    disabled={submitting}
                  />
                );
              })}
            </Box>

            <TextField
              id="feedback-message"
              label="Feedback"
              placeholder="What happened, or what would help?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={4}
              required
              fullWidth
              autoFocus
              disabled={submitting}
            />

            <TextField
              id="feedback-email"
              label="Email for follow-up (optional)"
              placeholder={authState.user?.email || "you@company.com"}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              disabled={submitting}
            />

            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !message.trim()}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? "Sending..." : "Send feedback"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="success">
          Thank you! Your feedback has been submitted.
        </Alert>
      </Snackbar>
    </>
  );
}

export default SendFeedback;
